import React from 'react';
import {
  Box, Paper, Stack, TextField, Button, Chip, Divider, MenuItem,
  IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Tooltip, Snackbar, Alert, Table, TableHead, TableRow, TableCell, TableBody,
  FormControlLabel, Checkbox, Card, CardHeader, CardContent, TableContainer, TablePagination
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add as AddIcon, Close as CloseIcon, FilterAlt as FilterIcon, FilterAltOff as FilterOffIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import Client from '../Client';
import Config from '../ClientConfig';

/**
 * InventoryProdSupplier.js
 *
 * Manage Product → Supplier info with filtering, create, and edit.
 * - Filters: product (autocomplete), supplier/company (autocomplete), active, preferred, central, limit, order-by-name
 * - Table: list of ProductSupplierInfo entries
 * - Dialog: add or edit (reuses same form)
 * - Saved card: shows last created/edited item with quick duplicate
 */

// Safe fallbacks so the page works even if Config.* is missing
const API = Config.API_URL_PRODUCT_SUPPLIER_INFO || '/api/1.0/productSupplierInfo/';
const API_PRODUCTS = (Config.API_URL_PRODUCT || '/api/1.0/product/') + 'list_products/';  // expects list_products with filters
const API_SUPPLIERS = (Config.API_URL_COMPANY || '/api/1.0/company/');     // /company/?isSupplier=1

function nf(n, d = 2) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(d) : '';
}

const compactFieldSx = {
  minWidth: 0,
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};

const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};

const compactTableSx = {
  tableLayout: 'fixed',
  minWidth: {
    xs: 980,
    sm: 1080,
    md: '100%',
  },
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: '#f8fafc',
    zIndex: 3,
  },
  '& .MuiTableCell-root': {
    py: { xs: 0.45, sm: 0.65 },
    px: { xs: 0.5, sm: 0.9 },
    fontSize: { xs: '0.72rem', sm: '0.84rem' },
  },
};

const compactPaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  minHeight: { xs: 38, sm: 50 },
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 38, sm: 50 },
    px: { xs: 0.25, sm: 1 },
    flexWrap: 'nowrap',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    m: 0,
  },
  '& .MuiIconButton-root': {
    p: { xs: 0.35, sm: 0.5 },
  },
};

export default class InventoryProdSupplier extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      // core
      client: new Client('none'),
      loading: false,
      saving: false,

      // lookups
      products: [], // [{ value, label }]
      suppliers: [], // [{ value, label }]

      // filters
      f_product: '',   // product iref (autocomplete)
      f_supplier: '',  // supplier iref (autocomplete)
      f_nameLike: '',  // optional fuzzy on product name/iref
      f_active: '',
      f_preferred: '', // '', true, false
      f_central: '',   // '', true, false
      f_limit: 200,
      orderByName: true,

      // data
      rows: [],
      // pagination
      page: 0,
      rowsPerPage: 10,

      // dialog
      dlgOpen: false,
      editMode: false,
      form: {
        iref: '',
        product: '',
        supplier: '',
        preferred: false,
        active: true,
        centralSupply: false,
        minQuantity: '',
        reorderQuantity: '',
        quantityMultiplier: 1,
      },

      // last saved banner
      lastSaved: null,

      // feedback
      snackOpen: false,
      snackMsg: '',
      snackSeverity: 'success',
      // modal-level error banner
      dialogError: '',
    };

    this.fetchLookups = this.fetchLookups.bind(this);
    this.fetchList = this.fetchList.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
    this.duplicateLast = this.duplicateLast.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
  }

  componentDidMount(){
    this.fetchLookups(() => this.fetchList());
  }

  // ----- helpers -----
  labelFromEntity = (val, fallbackStr = '') => {
    if (val == null) return fallbackStr;
    if (typeof val === 'string' || typeof val === 'number') return String(val);
    if (typeof val === 'object') {
      const name = val.name || val.productName || val.companyName || val.title;
      const code = val.iref || val.id || val.uid || val.slug;
      if (name) return String(name);
      if (code) return String(code);
      try { return JSON.stringify(val); } catch { return fallbackStr; }
    }
    return fallbackStr;
  };

  optionByValue(options, val){
    const v = String(val || '');
    for (let i=0;i<options.length;i++) if (String(options[i].value) === v) return options[i];
    return null;
  }

  asBool = (v) => (v === true || v === 1 || v === '1' || v === 'true' || v === 'True');
  boolToServer = (v) => (v ? 1 : 0);
  extractApiError(resp) {
    if (!resp) return 'No response';
    if (typeof resp === 'string') return resp;
    if (resp.error) return String(resp.error);
    if (resp.detail) return String(resp.detail);
    if (Array.isArray(resp.non_field_errors) && resp.non_field_errors.length) {
      return resp.non_field_errors.join(', ');
    }
    // DRF field errors: { field: ["msg"] }
    const parts = [];
    Object.entries(resp).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length && typeof v[0] === 'string') {
        parts.push(`${k}: ${v.join(', ')}`);
      }
    });
    return parts.length ? parts.join(' | ') : null;
  }

  // ----- lookups & list -----
  fetchLookups(cb){
    this.setState({ loading: true });
    try {
      // Only purchased products, active, ordered by name
      const productsResp = this.state.client.getData(API_PRODUCTS, { status: 2, isPurchased: 1, orderBy_name: 1 }) || {};
      // Suppliers endpoint returns { Companies: [...] }
      const suppliersResp = this.state.client.getData(API_SUPPLIERS, { isSupplier: 1 }) || {};

      const products = Array.isArray(productsResp.products) ? productsResp.products.map(p => ({
        value: String(p.iref || p.uid || p.id || ''),
        label: p.name || p.productName || p.description || String(p.iref || ''),
      })) : [];

      const rawSuppliers = Array.isArray(suppliersResp.Companies) ? suppliersResp.Companies
        : (Array.isArray(suppliersResp.suppliers) ? suppliersResp.suppliers
          : (Array.isArray(suppliersResp.companies) ? suppliersResp.companies
            : (Array.isArray(suppliersResp) ? suppliersResp : [])));
      const suppliers = rawSuppliers.map(s => ({
        value: String(s.iref || s.uid || s.id || ''),
        label: s.name || s.companyName || s.legalName || String(s.iref || ''),
      }));

      products.sort((a,b) => a.label.localeCompare(b.label));
      suppliers.sort((a,b) => a.label.localeCompare(b.label));

      this.setState({ products, suppliers, loading: false }, () => (cb && cb()));
    } catch(e){
      this.setState({ loading: false, snackOpen: true, snackMsg: 'Failed to load lookups', snackSeverity: 'error' });
      if (cb) cb();
    }
  }

  buildListParams(){
    const p = {};
    const s = this.state;
    if (s.f_product) p.product = s.f_product;         // product filter by iref
    if (s.f_supplier) p.supplier = s.f_supplier;       // company filter by iref
    if (s.f_nameLike) p.nameLike = s.f_nameLike;       // optional fuzzy
    if (s.f_active !== '') p.active = s.f_active ? 1 : 0;
    if (s.f_preferred !== '') p.preferred = s.f_preferred ? 1 : 0;
    if (s.f_central !== '') p.centralSupply = s.f_central ? 1 : 0;
    if (s.orderByName) p.orderBy_name = 1;
    if (s.f_limit) p.limit = s.f_limit;
    return p;
  }

  fetchList(){
    this.setState({ loading: true, page: 0 });
    try {
      const params = this.buildListParams();
      const resp = this.state.client.getData(API, params) || {};
      const rows = Array.isArray(resp.productSupplierInfo) ? resp.productSupplierInfo : [];
      this.setState({ rows, loading: false });
    } catch(e){
      this.setState({ loading: false, snackOpen: true, snackMsg: 'Failed to load data', snackSeverity: 'error' });
    }
  }

  resetFilters(){
    this.setState({
      f_product: '',
      f_supplier: '',
      f_nameLike: '',
      f_active: '',
      f_preferred: '',
      f_central: '',
      f_limit: 200,
      orderByName: true,
      page: 0,
    }, this.fetchList);
  }

  // ----- create / edit -----
  handleCreate(){
    this.setState({ dlgOpen: true, editMode: false, dialogError: '', form: {
      iref: '',
      product: '', supplier: '', preferred: false, active: true, centralSupply: false,
      minQuantity: '', reorderQuantity: '', quantityMultiplier: 1,
    }});
  }

  handleEdit(row){
    const f = {
      iref: row.iref || '',
      product: (row.product && row.product.iref) ? row.product.iref : (row.product || ''),
      supplier: (row.supplier && row.supplier.iref) ? row.supplier.iref : (row.supplier || ''),
      preferred: this.asBool(row.preferred),
      active: this.asBool(row.active),
      centralSupply: this.asBool(row.centralSupply),
      minQuantity: row.minQuantity ?? '',
      reorderQuantity: row.reorderQuantity ?? '',
      quantityMultiplier: row.quantityMultiplier ?? 1,
    };
    this.setState({ dlgOpen: true, editMode: true, dialogError: '', form: f });
  }

  validateForm(){
    const f = this.state.form;
    if (!f.product) return 'Select a product';
    if (!f.supplier) return 'Select a supplier';
    const qm = Number(f.quantityMultiplier);
    if (!Number.isFinite(qm) || qm < 1) return 'Quantity multiplier must be ≥ 1';
    const m = f.minQuantity === '' ? null : Number(f.minQuantity);
    const r = f.reorderQuantity === '' ? null : Number(f.reorderQuantity);
    if (m !== null && (!Number.isFinite(m) || m < 0)) return 'Min quantity must be ≥ 0';
    if (r !== null && (!Number.isFinite(r) || r < 0)) return 'Reorder quantity must be ≥ 0';
    return '';
  }

  saveSupplierInfo = (isEdit) => {
    const err = this.validateForm();
    if (err){
      this.setState({ snackOpen: true, snackMsg: err, snackSeverity: 'error' });
      return;
    }
    this.setState({ saving: true });

    const f = this.state.form;
    const payload = {
      product: String(f.product),
      supplier: String(f.supplier),
      preferred: this.boolToServer(f.preferred),
      active: this.boolToServer(f.active),
      centralSupply: this.boolToServer(f.centralSupply),
      minQuantity: f.minQuantity === '' ? null : Number(f.minQuantity),
      reorderQuantity: f.reorderQuantity === '' ? null : Number(f.reorderQuantity),
      quantityMultiplier: Number(f.quantityMultiplier) || 1,
    };

    let resp;
    if (isEdit) {
      const withIref = { ...payload, iref: String(f.iref) };
      resp = this.state.client.pushData(`${API}${f.iref}/edit/`, withIref);
    } else {
      // CREATE path
      resp = this.state.client.pushData(API, payload);
    }

    const errMsg = this.extractApiError(resp);
    if (!resp || errMsg){
      this.setState({
        saving: false,
        dialogError: errMsg || (isEdit ? 'Update failed' : 'Create failed'),
        snackOpen: true,
        snackMsg: errMsg || (isEdit ? 'Update failed' : 'Create failed'),
        snackSeverity: 'error'
      });
      return;
    }

    this.setState({
      saving: false,
      dlgOpen: false,
      editMode: false,
      lastSaved: resp,
      snackOpen: true,
      snackMsg: isEdit ? 'Supplier info updated' : 'Supplier info created',
      snackSeverity: 'success',
      dialogError: '',
    }, () => this.fetchList());
  }

  duplicateLast(){
    const s = this.state.lastSaved || {};
    const nextForm = { ...this.state.form };
    nextForm.product = String(s.product || nextForm.product || '');
    nextForm.supplier = String(s.supplier || nextForm.supplier || '');
    nextForm.preferred = !!s.preferred;
    nextForm.active = s.active !== undefined ? !!s.active : true;
    nextForm.centralSupply = !!s.centralSupply;
    nextForm.minQuantity = s.minQuantity ?? '';
    nextForm.reorderQuantity = s.reorderQuantity ?? '';
    nextForm.quantityMultiplier = s.quantityMultiplier ?? 1;
    this.setState({ form: nextForm, dlgOpen: true });
  }

  // Quick inline toggles using /edit/ action
  quickToggle(row, field){
    // robust boolean flip & include iref
    const current = (row[field] === true || row[field] === 1 || row[field] === '1' || row[field] === 'true' || row[field] === 'True');
    const next = !current;
    const payload = { iref: String(row.iref), [field]: next ? 1 : 0 };
    const resp = this.state.client.pushData(`${API}${row.iref}/edit/`, payload);
    const errMsg = this.extractApiError(resp);
    if (!resp || errMsg){
      this.setState({ snackOpen: true, snackMsg: errMsg || 'Update failed', snackSeverity: 'error' });
    } else {
      this.setState({ snackOpen: true, snackMsg: 'Updated', snackSeverity: 'success' }, this.fetchList);
    }
  }

  // ----- CSV helpers (UTF-8 BOM for diacritics / Excel) -----
  csvEscape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    // quote if contains delimiter, quotes, or newlines
    if (/[",;\n\r\t]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  downloadTextFile = (filename, text, mime = 'text/csv;charset=utf-8;') => {
    // BOM ensures Excel reads UTF-8 correctly (Romanian diacritics)
    const withBom = '\ufeff' + text;
    const blob = new Blob([withBom], { type: mime });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  fetchAllSupplierInfoForCsv = () => {
    const params = { ...this.buildListParams(), limit: 50000 };
    const resp = this.state.client.getData(API, params) || {};
    return Array.isArray(resp.productSupplierInfo) ? resp.productSupplierInfo : [];
  };

  exportVisibleToCsv = () => {
    try {
      // Export exactly what is currently loaded in the table (and already filtered)
      const rows = Array.isArray(this.state.rows) ? this.state.rows : [];

      if (!rows.length) {
        this.setState({ snackOpen: true, snackMsg: 'No rows to export.', snackSeverity: 'info' });
        return;
      }

      const headers = [
        'productIref',     // extra (not visible)
        'product',         // visible
        'supplier',        // visible
        'preferred',       // visible
        'centralSupply',   // visible
        'active',          // visible
        'minQuantity',     // visible
        'reorderQuantity', // visible
        'multiplier',      // visible
      ];

      const delim = ',';
      const lines = [headers.join(delim)];

      rows.forEach((r) => {
        // Match exactly the labels used in the UI
        const productLabel = this.labelFromEntity(r.productName ?? r.product, '');
        const supplierLabel = this.labelFromEntity(r.supplierName ?? r.supplier, '');

        // Add product iref (not visible, but requested)
        const productIref =
          (r.product && typeof r.product === 'object' && (r.product.iref || r.product.id || r.product.uid)) ||
          (typeof r.product === 'string' || typeof r.product === 'number' ? r.product : '') ||
          '';

        const row = [
          productIref,
          productLabel,
          supplierLabel,
          this.asBool(r.preferred) ? 'Yes' : 'No',
          this.asBool(r.centralSupply) ? 'Yes' : 'No',
          this.asBool(r.active) ? 'Yes' : 'No',
          r.minQuantity != null ? nf(r.minQuantity, 2) : '',
          r.reorderQuantity != null ? nf(r.reorderQuantity, 2) : '',
          r.quantityMultiplier ?? '',
        ].map(this.csvEscape);

        lines.push(row.join(delim));
      });

      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');

      this.downloadTextFile(`product_supplier_visible_${yyyy}-${mm}-${dd}.csv`, lines.join('\r\n'));
      this.setState({ snackOpen: true, snackMsg: `Exported ${rows.length} row(s)`, snackSeverity: 'success' });
    } catch (e) {
      this.setState({
        snackOpen: true,
        snackMsg: `Export failed: ${e?.message || e}`,
        snackSeverity: 'error',
      });
    }
  };

  // ----- render blocks -----
  renderFilters(){
    const boolOpts = [
      { value: '', label: 'All' },
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];

    return (
      <Paper
        elevation={1}
        sx={{
          flexShrink: 0,
          p: { xs: 0.5, sm: 0.75 },
          mb: 0.75,
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        <Stack spacing={0.75}>
          {/* Row 1: big selectors */}
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            sx={{
              '& .MuiAutocomplete-root': {
                flex: {
                  xs: '1 1 100%',
                  sm: '1 1 320px',
                },
                minWidth: 0,
              },
            }}
          >
            <Autocomplete
              size="small"
              sx={compactFieldSx}
              value={this.optionByValue(this.state.products, this.state.f_product)}
              options={this.state.products}
              isOptionEqualToValue={(o,v)=>String(o?.value||'')===String(v?.value||'')}
              onChange={(e,opt)=>this.setState({ f_product: opt ? String(opt.value) : '' })}
              renderInput={(params)=>(<TextField {...params} label="Filter by product" placeholder="Search product" fullWidth />)}
            />

            <Autocomplete
              size="small"
              sx={compactFieldSx}
              value={this.optionByValue(this.state.suppliers, this.state.f_supplier)}
              options={this.state.suppliers}
              isOptionEqualToValue={(o,v)=>String(o?.value||'')===String(v?.value||'')}
              onChange={(e,opt)=>this.setState({ f_supplier: opt ? String(opt.value) : '' })}
              renderInput={(params)=>(<TextField {...params} label="Filter by company" placeholder="Supplier" fullWidth />)}
            />
          </Stack>

          {/* Row 2: flags + actions aligned right */}
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            sx={{
              '& .MuiTextField-root': {
                minWidth: 0,
              },
              '& .MuiButton-root': compactButtonSx,
            }}
          >
            <TextField
              label="Name/iref contains"
              size="small"
              value={this.state.f_nameLike}
              onChange={(e)=>this.setState({ f_nameLike: e.target.value })}
              onKeyDown={(e)=>{ if (e.key === 'Enter') this.fetchList(); }}
              sx={{
                ...compactFieldSx,
                width: {
                  xs: '100%',
                  sm: 220,
                },
              }}
            />

            <TextField select size="small" label="Active" value={String(this.state.f_active)}
              onChange={(e)=>{ const v = e.target.value; this.setState({ f_active: v === '' ? '' : (v === 'true') }); }} 
              sx={{
                ...compactFieldSx,
                width: {
                  xs: 'calc(50% - 6px)',
                  sm: 145,
                },
              }}
            >
              {boolOpts.map(o => <MenuItem key={String(o.value)} value={String(o.value)}>{o.label}</MenuItem>)}
            </TextField>

            <TextField select size="small" label="Preferred" value={String(this.state.f_preferred)}
              onChange={(e)=>{ const v = e.target.value; this.setState({ f_preferred: v === '' ? '' : (v === 'true') }); }} 
              sx={{
                ...compactFieldSx,
                width: {
                  xs: 'calc(50% - 6px)',
                  sm: 145,
                },
              }}
            >
              {boolOpts.map(o => <MenuItem key={String(o.value)} value={String(o.value)}>{o.label}</MenuItem>)}
            </TextField>

            <TextField select size="small" label="Central" value={String(this.state.f_central)}
              onChange={(e)=>{ const v = e.target.value; this.setState({ f_central: v === '' ? '' : (v === 'true') }); }} 
              sx={{
                ...compactFieldSx,
                width: {
                  xs: 'calc(50% - 6px)',
                  sm: 145,
                },
              }}
            >
              {boolOpts.map(o => <MenuItem key={String(o.value)} value={String(o.value)}>{o.label}</MenuItem>)}
            </TextField>

            <TextField size="small" label="Limit" type="number" value={this.state.f_limit}
              onChange={(e)=>this.setState({ f_limit: Number(e.target.value) || 0 })} 
              sx={{
                ...compactFieldSx,
                width: {
                  xs: 'calc(50% - 6px)',
                  sm: 100,
                },
              }} 
            />

            <FormControlLabel control={<Checkbox checked={this.state.orderByName} onChange={(e)=>this.setState({ orderByName: e.target.checked })} />} label="Order by name" />

            <Box sx={{ flexGrow: 1 }} />

            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
                flexWrap: 'wrap',
                '& .MuiButton-root': {
                  ...compactButtonSx,
                  flex: {
                    xs: '1 1 auto',
                    sm: '0 0 auto',
                  },
                },
              }}
            >
              <Button variant="contained" startIcon={<FilterIcon />} onClick={this.fetchList}>Apply filters</Button>
              <Button variant="text" startIcon={<FilterOffIcon />} onClick={this.resetFilters}>Clear</Button>
              <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={this.exportVisibleToCsv}>Export CSV</Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={this.handleCreate}>New</Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  renderTable(){
    const rows = Array.isArray(this.state.rows) ? this.state.rows : [];
    const page = this.state.page || 0;
    const rowsPerPage = this.state.rowsPerPage || 10;
    const pagedRows = rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;

    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          borderRadius: '14px',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexShrink: 0,
            px: { xs: 0.75, sm: 1 },
            py: 0.5,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          }}
        >
          <Typography variant="subtitle2">Results</Typography>
          <Chip size="small" label={`${rows.length} item(s)`} />
        </Stack>
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            borderRadius: 0,
          }}
        >
          <Table stickyHeader size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Supplier</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Preferred</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Central</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Active</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Min Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Reorder Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Multiplier</TableCell>
                <TableCell sx={{ fontWeight: 700 }}align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((r, idx) => (
                <TableRow key={String(r.iref || idx)} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{this.labelFromEntity(r.productName ?? r.product, '')}</TableCell>
                  <TableCell>{this.labelFromEntity(r.supplierName ?? r.supplier, '')}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={this.asBool(r.preferred) ? 'Yes' : 'No'}  color={this.asBool(r.preferred) ? 'success' : 'default'} clickable onClick={()=>this.quickToggle(r, 'preferred')} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={this.asBool(r.centralSupply) ? 'Yes' : 'No'}  color={this.asBool(r.centralSupply) ? 'primary' : 'default'} clickable onClick={()=>this.quickToggle(r, 'centralSupply')} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={this.asBool(r.active) ? 'Yes' : 'No'}  color={this.asBool(r.active) ? 'success' : 'default'} clickable onClick={()=>this.quickToggle(r, 'active')} />
                  </TableCell>
                  <TableCell align="right">{r.minQuantity != null ? nf(r.minQuantity, 2) : ''}</TableCell>
                  <TableCell align="right">{r.reorderQuantity != null ? nf(r.reorderQuantity, 2) : ''}</TableCell>
                  <TableCell align="right">{r.quantityMultiplier}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={()=>this.handleEdit(r)}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Alert severity="info">No items match your filters.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider sx={{ my: 0.5 }} />
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(e, newPage)=>this.setState({ page: newPage })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e)=>this.setState({ rowsPerPage: Number(e.target.value) || 10, page: 0 })}
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          showFirstButton
          showLastButton
          sx={compactPaginationSx}
        />
      </Paper>
    );
  }

  renderCreateDialog(){
    const f = this.state.form;
    const productOpt = this.optionByValue(this.state.products, f.product);
    const supplierOpt = this.optionByValue(this.state.suppliers, f.supplier);

    return (
      <Dialog
        open={this.state.dlgOpen}
        onClose={() => {
          // do nothing on backdrop / esc
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{this.state.editMode ? 'Edit supplier info' : 'Add supplier info'}</Typography>
            <IconButton onClick={()=>this.setState({ dlgOpen: false, editMode: false })}><CloseIcon/></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {this.state.dialogError && (
            <Alert severity="error" onClose={()=>this.setState({ dialogError: '' })} sx={{ mb: 2 }}>
              {this.state.dialogError}
            </Alert>
          )}
          <Stack spacing={2}>
            <Autocomplete
              size="small"
              value={productOpt}
              options={this.state.products}
              isOptionEqualToValue={(o,v)=>String(o?.value||'')===String(v?.value||'')}
              onChange={(e, opt)=>this.setState({ form: { ...this.state.form, product: opt ? String(opt.value) : '' }})}
              renderInput={(params)=>(<TextField {...params} label="Product (purchased)" placeholder="Search product" />)}
            />

            <Autocomplete
              size="small"
              value={supplierOpt}
              options={this.state.suppliers}
              isOptionEqualToValue={(o,v)=>String(o?.value||'')===String(v?.value||'')}
              onChange={(e, opt)=>this.setState({ form: { ...this.state.form, supplier: opt ? String(opt.value) : '' }})}
              renderInput={(params)=>(<TextField {...params} label="Supplier" placeholder="Search supplier" />)}
            />

            <Stack direction="row" spacing={2}>
              <TextField label="Min quantity" type="number" size="small" value={f.minQuantity}
                onChange={(e)=>this.setState({ form: { ...this.state.form, minQuantity: e.target.value }})}
                inputProps={{ step: 0.01, min: 0 }} sx={{ width: 180 }} />
              <TextField label="Reorder quantity" type="number" size="small" value={f.reorderQuantity}
                onChange={(e)=>this.setState({ form: { ...this.state.form, reorderQuantity: e.target.value }})}
                inputProps={{ step: 0.01, min: 0 }} sx={{ width: 200 }} />
              <TextField label="Quantity multiplier" type="number" size="small" value={f.quantityMultiplier}
                onChange={(e)=>this.setState({ form: { ...this.state.form, quantityMultiplier: e.target.value }})}
                inputProps={{ step: 1, min: 1 }} sx={{ width: 200 }} />
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Checkbox checked={f.preferred} onChange={(e)=>this.setState({ form: { ...this.state.form, preferred: e.target.checked }})} />} label="Preferred" />
              <FormControlLabel control={<Checkbox checked={f.centralSupply} onChange={(e)=>this.setState({ form: { ...this.state.form, centralSupply: e.target.checked }})} />} label="Central supply" />
              <FormControlLabel control={<Checkbox checked={f.active} onChange={(e)=>this.setState({ form: { ...this.state.form, active: e.target.checked }})} />} label="Active" />
            </Stack>

            <Alert severity="info">
              If no other preferred supplier exists for the selected product, this record will be marked as preferred automatically by the backend.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>this.setState({ dlgOpen: false, editMode: false })}>Cancel</Button>
          <Button variant="contained" onClick={()=>this.saveSupplierInfo(this.state.editMode)} disabled={this.state.saving}>
            {this.state.saving ? 'Saving…' : (this.state.editMode ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderSavedCard(){
    const s = this.state.lastSaved;
    if (!s) return null;

    const pLabel = (this.state.products.find(x => String(x.value) === String(s.product)) || {}).label
                 || this.labelFromEntity(s.productName ?? s.product, '');
    const cLabel = (this.state.suppliers.find(x => String(x.value) === String(s.supplier)) || {}).label
                 || this.labelFromEntity(s.supplierName ?? s.supplier, '');

    return (
      <Card
        sx={{
          flexShrink: 0,
          mt: 0.75,
          borderRadius: '14px',
          boxShadow: 1,
          display: {
            xs: 'none',
            md: 'block',
          },
        }}
      >
        <CardHeader title={<Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Supplier info saved</Typography>
          {s.iref && <Chip label={'#'+s.iref} size="small"/>}
          <Chip label={pLabel} size="small" color="primary"/>
          <Chip label={cLabel} size="small"/>
          {s.preferred && <Chip label="Preferred" size="small" color="success"/>}
          {s.centralSupply && <Chip label="Central" size="small"/>}
          {s.active ? <Chip label="Active" size="small" color="success"/> : <Chip label="Inactive" size="small"/>}
        </Stack>} action={<Stack direction="row" spacing={1}>
          <Tooltip title="Duplicate into Create dialog"><Button size="small" onClick={this.duplicateLast}>Duplicate</Button></Tooltip>
        </Stack>} />
        <CardContent>
          <Stack direction="row" spacing={2}>
            <Chip label={'Min ' + (s.minQuantity != null ? nf(s.minQuantity,2) : '-') } />
            <Chip label={'Reorder ' + (s.reorderQuantity != null ? nf(s.reorderQuantity,2) : '-') } />
            <Chip label={'Multiplier ' + (s.quantityMultiplier || 1)} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  render(){
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderFilters()}

        {this.state.loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 80,
              right: 24,
              zIndex: 20,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {this.renderTable()}

        {this.renderSavedCard()}

        {this.renderCreateDialog()}

        <Snackbar
          open={this.state.snackOpen}
          autoHideDuration={4000}
          onClose={()=>this.setState({ snackOpen:false })}
          message={this.state.snackMsg}
        />
      </Box>
    );
  }
}